import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { FormHelperText } from '@material-ui/core';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import { colors, borderRadius } from '../../constants/styleConstants';
import { dateFormats } from '../../constants/consts';
import { styles } from './FormikAutocompleteSelect';
import { createDate } from '../../utils/date';

const TodayIcon = dynamic(() => import('@material-ui/icons/Today'));
const Clear = dynamic(() => import('@material-ui/icons/Clear'));

const useStyles = makeStyles(() => ({
  field: {
    height: '100%',
    maxHeight: '64px',
    width: '100%',
    paddingTop: 16,
    position: 'relative',
    '& .DayPickerInput': {
      width: '100%',
      height: '100%',
      lineHeight: 3.2,
      '& input': {
        width: '100%',
        height: '100%',
        padding: '8px 10px 8px 40px',
        border: `1px solid ${colors.fieldsBorderGray}`,
        borderRadius: borderRadius.fields,
        backgroundColor: colors.white,
        '&:focus': {
          backgroundColor: colors.background.gray,
          outline: 'none',
        },
      },
    },
    '& .DayPickerInput-Overlay': {
      zIndex: 10,
    },
  },
  label: {
    position: 'absolute',
    left: 0,
    top: 0,
    color: colors.title,
    letterSpacing: 0.6,
    transformOrigin: 'left',
  },
  calendarIcon: {
    fill: colors.blue,
    position: 'absolute',
    left: 6,
    top: 'calc(50% - 3px)',
  },
  clearIcon: {
    position: 'absolute',
    right: 6,
    top: '50%',
    fill: colors.blue,
    fontSize: '1rem',
  },
  overlay: {
    '& .DayPicker-Caption': {
      textAlign: 'center',
    },
    '& .DayPicker-NavButton--prev': {
      left: 5,
    },
    '& .DayPicker-NavButton--next': {
      right: 5,
    },
    '& .DayPicker-Day': {
      padding: 8,
      lineHeight: 1,
      '&--today': {
        color: `${colors.primary} !important`,
      },
      '&--selected': {
        backgroundColor: `${colors.primary} !important`,
        color: `${colors.white} !important`,
      },
    },
  },
  error: {
    '& .DayPickerInput': {
      '& input': {
        borderColor: colors.red,
      },
    },
  },
  errorColor: {
    color: colors.red,
  },
  required: {
    color: colors.primary,
    lineHeight: 1,
  },
  shrinkFieldClass: styles.shrink,
}));

const YearMonthForm = React.memo(function caption({ date, localeUtils, onChange, fromMonth, toMonth }) {
  const months = localeUtils.getMonths();

  const years = [];
  for (let i = fromMonth.getFullYear(); i <= toMonth.getFullYear(); i += 1) {
    years.push(i);
  }

  const handleChange = function handleChange(e) {
    const { year, month } = e.target.form;
    onChange(new Date(year.value, month.value));
  };

  return (
    <div className="DayPicker-Caption">
      <select name="month" onChange={handleChange} value={date.getMonth()}>
        {months.map((month, i) => (
          <option key={month} value={i}>
            {month}
          </option>
        ))}
      </select>
      <select name="year" onChange={handleChange} value={date.getFullYear()}>
        {years.map(year => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </select>
    </div>
  );
});

YearMonthForm.propTypes = {
  date: PropTypes.object,
  localeUtils: PropTypes.any,
  onChange: PropTypes.func,
};

export class CalendarInput extends PureComponent {
  constructor(props) {
    super(props);
    this.inputRef = React.createRef();
  }

  focus() {
    this.inputRef.current.focus();
  }

  render = () => {
    let { value } = this.props;
    if (typeof value === 'undefined') {
      value = '';
    }

    return <input {...this.props} value={value} ref={this.inputRef} />;
  };
}

const CalendarField = ({
  className,
  label,
  placeholder,
  onChange,
  selectedDay,
  disabledDays,
  isClearable,
  format,
  valueAfterClear,
  fromMonth,
  toMonth,
  required,
  errorText,
  hasError,
  overrides,
  asTag,
}) => {
  const defaultClasses = useStyles();
  const classes = overrides && overrides.classes ? overrides.classes : defaultClasses;
  const [value, setValue] = React.useState('');
  const [month, setMonth] = React.useState('');
  const dayInputPickerRef = React.useRef();

  React.useEffect(() => {
    setValue(selectedDay);
  }, [selectedDay]);

  const handleDayChange = selectedNewDay => {
    setValue(selectedNewDay);
    onChange(selectedNewDay);
  };

  const handleYearMonthChange = newMonth => {
    setMonth(newMonth);
  };

  const TagName = asTag === 'form' ? 'form' : 'div';
  return (
    <>
      <TagName className={classnames(classes.field, className, { [classes.error]: hasError })}>
        {label && (
          <label className={classnames(classes.label, classes.shrinkFieldClass, { [classes.errorColor]: hasError })}>
            {label}
            {required && <span className={classes.required}>*</span>}
          </label>
        )}
        <DayPickerInput
          ref={dayInputPickerRef}
          value={value}
          placeholder={placeholder}
          formatDate={date => createDate(date).format(format)}
          onDayChange={handleDayChange}
          component={CalendarInput}
          keepFocus={false}
          dayPickerProps={{
            month,
            fromMonth,
            toMonth,
            showOutsideDays: true,
            disabledDays,
            captionElement: ({ date, localeUtils }) => (
              <YearMonthForm
                date={date}
                localeUtils={localeUtils}
                onChange={handleYearMonthChange}
                fromMonth={fromMonth}
                toMonth={toMonth}
              />
            ),
            className: classes.overlay,
            initialMonth: disabledDays[0]?.after || disabledDays[0]?.before || new Date(),
          }}
        />
        <TodayIcon
          className={classes.calendarIcon}
          onClick={() => {
            const { input: calendarInput } = dayInputPickerRef.current;
            const inputControl = calendarInput.inputRef.current;
            // eslint-disable-next-line no-unused-expressions
            inputControl?.focus();
          }}
        />
        {isClearable && value && (
          <Clear className={classes.clearIcon} onClick={() => handleDayChange(valueAfterClear)} />
        )}
      </TagName>
      {errorText && (
        <FormHelperText
          className={classnames(classes.formHelperText, {
            [classes.errorColor]: hasError,
          })}
        >
          {errorText}
        </FormHelperText>
      )}
    </>
  );
};

CalendarField.propTypes = {
  className: PropTypes.string,
  onChange: PropTypes.func,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  selectedDay: PropTypes.string,
  disabledDays: PropTypes.array,
  isClearable: PropTypes.bool,
  format: PropTypes.string,
  valueAfterClear: PropTypes.string,
  fromMonth: PropTypes.instanceOf(Date),
  toMonth: PropTypes.instanceOf(Date),
  overrides: PropTypes.any,
  asTag: PropTypes.string,
};

CalendarField.defaultProps = {
  disabledDays: [],
  isClearable: true,
  format: dateFormats.basic,
  placeholder: 'YYYY-MM-DD',
  valueAfterClear: null,
  fromMonth: new Date(1996, 11), // TODO set currentYear - 2 if user has no subscription
  toMonth: new Date(new Date().getFullYear() + 5, 11),
  overrides: {},
  asTag: 'div',
};

export default CalendarField;
